import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import { AlertTriangle, RotateCcw, ArrowLeft, HelpCircle, Zap, CreditCard, Package, Info, Menu } from 'lucide-react';

const PaymentCancel: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const textPrimary = isDark ? '#E2E8F0' : '#314158';
  const textSecondary = isDark ? '#94A3B8' : '#64748B';
  const infoBg = isDark ? 'rgba(43, 127, 255, 0.08)' : '#EFF6FF';
  const infoBorder = isDark ? '#1C398E' : '#BFDBFE';
  const infoText = isDark ? '#93C5FD' : '#1E40AF';
  const infoLabel = isDark ? '#93C5FD' : '#1D4ED8';
  const btnSecondaryBg = isDark ? '#1D293D' : '#FFFFFF';
  const btnSecondaryBorder = isDark ? '#314158' : '#E2E8F0';
  const btnSecondaryText = isDark ? '#E2E8F0' : '#314158';
  const sectionTitle = isDark ? '#CBD5E1' : '#475569';
  const actionItemBg = isDark ? '#162032' : '#F8FAFC';
  const actionItemBorder = isDark ? '#1D293D' : '#E2E8F0';
  const actionItemText = isDark ? '#CBD5E1' : '#475569';

  const handleRetry = () => {
    navigate('/subscriptions');
  };

  const handleGoBack = () => {
    navigate('/subscriptions');
  };

  return (
    <div
      className="flex flex-col items-start h-full font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{ gap: '8px', background: containerBg }}
    >
      <div
        className="flex flex-col items-start flex-1 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{ background: mainBg, boxShadow: mainShadow }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 w-full flex-shrink-0"
          style={{
            height: '84px',
            padding: '24px 24px',
            borderBottom: `1px solid ${headerBorder}`,
          }}
        >
          <button
            className="lg:hidden flex items-center justify-center flex-shrink-0"
            style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            onClick={toggleSidebar}
          >
            <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
          </button>
          <div
            className="hidden lg:flex"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '12px',
              background: isDark ? '#422006' : '#FEF3C7',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={20} color={isDark ? '#FBBF24' : '#D97706'} />
          </div>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: 700, color: titleColor, margin: 0, lineHeight: '1.2' }}>
              {t('payment.cancel.title')}
            </h1>
            <p style={{ fontSize: '12px', color: subtitleColor, margin: 0, marginTop: '2px' }}>
              {t('payment.cancel.description')}
            </p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto w-full" style={{ padding: '24px' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '20px' }}>
            {/* Left Column - Main Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Payment Status Card */}
              <div
                style={{
                  background: cardBg,
                  borderRadius: '12px',
                  border: `1px solid ${cardBorder}`,
                  overflow: 'hidden',
                }}
              >
                {/* Card Header */}
                <div
                  style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${cardBorder}`,
                    background: isDark ? 'rgba(245, 158, 11, 0.06)' : '#FFFBEB',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <AlertTriangle size={14} color={isDark ? '#FBBF24' : '#D97706'} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: isDark ? '#FBBF24' : '#92400E' }}>
                    {t('payment.cancel.title')}
                  </span>
                </div>

                {/* Card Body */}
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: isDark ? 'rgba(245, 158, 11, 0.1)' : '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <AlertTriangle size={18} color={isDark ? '#FBBF24' : '#D97706'} />
                    </div>
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: 600, color: textPrimary, margin: 0 }}>
                        {t('payment.cancel.title')}
                      </p>
                      <p style={{ fontSize: '12px', color: textSecondary, margin: 0, marginTop: '2px' }}>
                        {t('payment.cancel.description')}
                      </p>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div
                    style={{
                      background: infoBg,
                      borderLeft: `3px solid ${isDark ? '#2B7FFF' : '#3B82F6'}`,
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                    }}
                  >
                    <p style={{ fontSize: '12px', fontWeight: 600, color: infoLabel, margin: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Info size={13} />
                      {t('payment.cancel.note')}:
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <li style={{ fontSize: '12px', color: infoText }}>{t('payment.cancel.point1')}</li>
                      <li style={{ fontSize: '12px', color: infoText }}>{t('payment.cancel.point2')}</li>
                      <li style={{ fontSize: '12px', color: infoText }}>{t('payment.cancel.point3')}</li>
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '10px', paddingTop: '12px', borderTop: `1px solid ${cardBorder}` }}>
                    <button
                      onClick={handleRetry}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                        color: '#FFFFFF',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      <RotateCcw size={14} />
                      {t('payment.cancel.retry')}
                    </button>
                    <button
                      onClick={handleGoBack}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: btnSecondaryBg,
                        color: btnSecondaryText,
                        borderRadius: '8px',
                        border: `1px solid ${btnSecondaryBorder}`,
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      <ArrowLeft size={14} />
                      {t('payment.cancel.goBack')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Help & Quick Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Need Help Card */}
              <div
                style={{
                  background: cardBg,
                  borderRadius: '12px',
                  border: `1px solid ${cardBorder}`,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${cardBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <HelpCircle size={14} color="#2B7FFF" />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: sectionTitle }}>
                    {t('help.stillNeedHelp') || 'Need Help?'}
                  </span>
                </div>
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <p style={{ fontSize: '12px', color: textSecondary, margin: 0, lineHeight: '1.6' }}>
                    {t('help.contactSupportMessage') || 'If you experienced any issues during the payment process, our support team is here to help.'}
                  </p>
                  <button
                    onClick={() => navigate('/help')}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: infoBg,
                      color: '#2B7FFF',
                      borderRadius: '8px',
                      border: `1px solid ${infoBorder}`,
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <HelpCircle size={14} />
                    {t('help.contactSupport') || 'Contact Support'}
                  </button>
                </div>
              </div>

              {/* Quick Actions Card */}
              <div
                style={{
                  background: cardBg,
                  borderRadius: '12px',
                  border: `1px solid ${cardBorder}`,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${cardBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Zap size={14} color="#2B7FFF" />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: sectionTitle }}>
                    {t('common.quickActions') || 'Quick Actions'}
                  </span>
                </div>
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { icon: <Zap size={14} color="#2B7FFF" />, label: t('subscriptions.upgradePlan') || 'View Plans', path: '/subscriptions' },
                    { icon: <CreditCard size={14} color={isDark ? '#4ADE80' : '#22C55E'} />, label: t('billing.title') || 'Billing', path: '/billing' },
                    { icon: <Package size={14} color={isDark ? '#C084FC' : '#8B5CF6'} />, label: t('navigation.subscriptions') || 'Subscriptions', path: '/subscriptions' },
                  ].map((action, i) => (
                    <button
                      key={i}
                      onClick={() => navigate(action.path)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: actionItemBg,
                        color: actionItemText,
                        borderRadius: '8px',
                        border: `1px solid ${actionItemBorder}`,
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        textAlign: 'left',
                      }}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancel;
